import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { BasicButton, ButtonType, Icon, IconSize } from "@gaming/xds-react-js";
import { Upload } from "@gaming/xds-react-js-icons";

import { TaxIdType } from "@gamedev-web/one-gaming-crm";

import { Modal, ModalInitialFocus } from "../../components/Modal/Modal";
import { PageLoadingSpinner } from "../../components/PageLoadingSpinner";
import { PartnerPageLayout } from "../../components/PartnerPageLayout";
import { partnerSectionLinks } from "../../constants";
import { useRequireAuth } from "../../constants/urlsClientSide";
import { useLocalizedCountriesOrRegionsList } from "../../helpers/countriesOrRegionsList";
import {
  BillingRuleQuestion,
  BillingTaxField,
  ChileVatStatus,
  getBillingTaxIdTypeValue,
  resolveDevelopmentHardwareBillingRule,
} from "../../helpers/developmentHardwareBillingRules";
import { PartnerAccessLevel, usePartnerRouteGuard } from "../../helpers/usePartnerRouteGuard";
import { useStrings } from "../../localization/localizationHelpers";
import {
  BillingAccount,
  CreateBillingAccountRequest,
  DevelopmentHardwareStoreStatus,
  DeviceOrderingAddressesResponse,
} from "../../models/developmentHardware";
import { getXboxOnboardData } from "../../redux/selectors/xboxOnboardSelectors";
import { getAvatarClass } from "../AdminPage/avatarHelpers";
import * as styles from "./PartnerAddBillingAccountPage.module.css";

const addressesUrl = "/games/partner/development-hardware-addresses";
const getBillingAccountUrl = (billingAccountId: string) =>
  `/games/partner/development-hardware-billing-accounts/${billingAccountId}`;
const getBillingAccountFileUrl = (billingAccountId: string, fileId: string) =>
  `/games/partner/development-hardware-billing-accounts/${billingAccountId}/files/${fileId}`;
const maxSupportingDocumentBytes = 50 * 1024 * 1024;

type PendingSupportingDocument = Readonly<{
  id: string;
  file: File;
}>;

const billingRuleStringKeys = [
  "developmentHardwareTaxInformationTitle",
  "developmentHardwareHasVatIdQuestion",
  "developmentHardwareChileVatStatusQuestion",
  "developmentHardwareVatExempt",
  "developmentHardwareVatLiable",
  "developmentHardwareTaxIdType",
  "developmentHardwareIndiaGst",
  "developmentHardwareIndiaPan",
  "developmentHardwareGoldenTaxInformation",
  "developmentHardwareSupportingDocuments",
  "developmentHardwareTaxIdLabel",
  "developmentHardwareVatIdLabel",
  "developmentHardwareJapanTaxIdLabel",
  "developmentHardwareAbnLabel",
  "developmentHardwareCnpjLabel",
  "developmentHardwareCpfLabel",
  "developmentHardwareRutLabel",
  "developmentHardwareIndiaTaxIdLabel",
  "developmentHardwareKoreaTaxIdLabel",
  "developmentHardwareRfcLabel",
  "developmentHardwareNzbnLabel",
  "developmentHardwareDocumentVatCertificate",
  "developmentHardwareDocumentValidationMismatch",
  "developmentHardwareDocumentCnpj",
  "developmentHardwareDocumentCpf",
  "developmentHardwareDocumentChileTaxForm",
  "developmentHardwareDocumentBusinessRegistration",
  "developmentHardwareDocumentIndia",
  "developmentHardwareDocumentKoreaBusiness",
  "developmentHardwareDocumentGovernmentId",
  "developmentHardwareDocumentMexicoTaxStatus",
  "welcomePartnerDragAndDropInstructions",
  "welcomePartnerFileUploadBrowseFile",
] as const;

type BillingRuleStringKey = (typeof billingRuleStringKeys)[number];

const getBillingTaxLabelKey = (
  isoCode: string,
  field: BillingTaxField,
  isSoleProprietor: boolean
): BillingRuleStringKey => {
  switch (isoCode) {
    case "AU":
      return "developmentHardwareAbnLabel";
    case "BR":
      return isSoleProprietor ? "developmentHardwareCpfLabel" : "developmentHardwareCnpjLabel";
    case "CL":
      return "developmentHardwareRutLabel";
    case "IN":
      return "developmentHardwareIndiaTaxIdLabel";
    case "JP":
      return "developmentHardwareJapanTaxIdLabel";
    case "KR":
      return "developmentHardwareKoreaTaxIdLabel";
    case "MX":
      return "developmentHardwareRfcLabel";
    case "NZ":
      return "developmentHardwareNzbnLabel";
    default:
      return field === "vatId" ? "developmentHardwareVatIdLabel" : "developmentHardwareTaxIdLabel";
  }
};

const getBillingDocumentGuidanceKey = (
  isoCode: string,
  isSoleProprietor: boolean
): BillingRuleStringKey => {
  switch (isoCode) {
    case "AU":
    case "NZ":
      return "developmentHardwareDocumentValidationMismatch";
    case "BR":
      return isSoleProprietor
        ? "developmentHardwareDocumentCpf"
        : "developmentHardwareDocumentCnpj";
    case "CL":
      return "developmentHardwareDocumentChileTaxForm";
    case "CN":
    case "HK":
    case "TW":
      return "developmentHardwareDocumentBusinessRegistration";
    case "IN":
      return "developmentHardwareDocumentIndia";
    case "KR":
      return isSoleProprietor
        ? "developmentHardwareDocumentGovernmentId"
        : "developmentHardwareDocumentKoreaBusiness";
    case "MX":
      return "developmentHardwareDocumentMexicoTaxStatus";
    default:
      return "developmentHardwareDocumentVatCertificate";
  }
};

const emptyForm: CreateBillingAccountRequest = {
  locationName: "",
  financialContactId: "",
  line1: "",
  line2: "",
  city: "",
  stateOrProvince: "",
  postalCode: "",
  phone: "",
  countryId: "",
  taxIdType: undefined,
  vatId: "",
  taxIdNumber: "",
  vatLiable: false,
  chineseGoldenAddress: "",
};

const getContactInitials = (name: string, email?: string) => {
  const nameParts = name.trim().split(/\s+/).filter(Boolean);
  if (nameParts.length > 1) {
    return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(
      0
    )}`.toLocaleUpperCase();
  }
  return (nameParts[0]?.slice(0, 2) ?? email?.charAt(0) ?? "").toLocaleUpperCase();
};

export const PartnerAddBillingAccountPage = () => {
  const isPageReady = usePartnerRouteGuard(PartnerAccessLevel.NdaSigned);
  const isSignedIn = useRequireAuth();
  const data = useSelector(getXboxOnboardData);
  const history = useHistory();
  const { billingAccountId } = useParams<{ billingAccountId: string }>();
  const [
    pageLabel,
    billingInformationTitle,
    billingInformationDescription,
    billingAccountNameLabel,
    financialContactLabel,
    financialContactDescription,
    changeFinancialContactLabel,
    changeFinancialContactTitle,
    addressLine1Label,
    addressLine2Label,
    cityLabel,
    stateLabel,
    postalCodeLabel,
    phoneLabel,
    countryLabel,
    selectLabel,
    yesLabel,
    noLabel,
    cancelLabel,
    submitLabel,
    requiredFieldsError,
    submitError,
    statusLabel,
    underReviewLabel,
    closedStatusLabel,
  ] = useStrings(
    "deviceOrderingAddressesLabel",
    "developmentHardwareBillingInformationTitle",
    "developmentHardwareBillingInformationDescription",
    "developmentHardwareBillingAccountName",
    "developmentHardwareFinancialContact",
    "developmentHardwareFinancialContactDescription",
    "developmentHardwareChangeFinancialContact",
    "developmentHardwareChangeFinancialContactTitle",
    "xboxOnboardAddressLine1",
    "addressLine2",
    "city",
    "stateRegion",
    "postalCode",
    "xboxOnboardPhone",
    "countryOrRegion",
    "select",
    "yes",
    "no",
    "cancelButton",
    "xboxOnboardSubmitButton",
    "requiredField",
    "saveSubmitFormErrorMessage",
    "status",
    "underReview",
    "developmentHardwareStoreStatusClosed"
  );
  const billingRuleStringValues = useStrings(...billingRuleStringKeys);
  const billingRuleStrings = useMemo(
    () =>
      Object.fromEntries(
        billingRuleStringKeys.map((key, index) => [key, billingRuleStringValues[index]])
      ) as Readonly<Record<(typeof billingRuleStringKeys)[number], string>>,
    [billingRuleStringValues]
  );
  const [form, setForm] = useState<CreateBillingAccountRequest>(emptyForm);
  const [options, setOptions] = useState<DeviceOrderingAddressesResponse>();
  const [billingAccount, setBillingAccount] = useState<BillingAccount>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFinancialContactModalOpen, setIsFinancialContactModalOpen] = useState(false);
  const [pendingFinancialContactId, setPendingFinancialContactId] = useState("");
  const [hasVatId, setHasVatId] = useState<boolean>();
  const [chileVatStatus, setChileVatStatus] = useState<ChileVatStatus>("");
  const [supportingDocuments, setSupportingDocuments] = useState<
    ReadonlyArray<PendingSupportingDocument>
  >([]);
  const [savedBillingAccountId, setSavedBillingAccountId] = useState("");
  const [error, setError] = useState("");
  const countryOptions = options?.countriesOrRegions
    ? billingAccount?.countryId &&
      !options.countriesOrRegions.some(country => country.id === billingAccount.countryId)
      ? billingAccount.countryIsoCode && billingAccount.billingRuleRegion
        ? [
            ...options.countriesOrRegions,
            {
              id: billingAccount.countryId,
              isoCode: billingAccount.countryIsoCode,
              region: billingAccount.billingRuleRegion,
              value: billingAccount.countryName,
            },
          ]
        : options.countriesOrRegions
      : options.countriesOrRegions
    : undefined;
  const countries = useLocalizedCountriesOrRegionsList(countryOptions, false);
  const selectedCountry = countryOptions?.find(country => country.id === form.countryId);
  const billingRule = useMemo(
    () =>
      selectedCountry?.isoCode && selectedCountry.region
        ? resolveDevelopmentHardwareBillingRule({
            isoCode: selectedCountry.isoCode,
            region: selectedCountry.region,
            facts: {
              isSoleProprietor: data?.studio?.isSoleProprietor === true,
              hasVatId,
              chileVatStatus,
              taxIdValidationMismatch: false,
            },
          })
        : undefined,
    [chileVatStatus, data?.studio?.isSoleProprietor, hasVatId, selectedCountry]
  );
  const showTaxSection =
    billingRule !== undefined &&
    (billingRule.taxId.field !== "none" ||
      billingRule.questions.length > 0 ||
      billingRule.attachment !== undefined ||
      billingRule.additionalDetails !== undefined);

  useEffect(() => {
    if (!isPageReady || !isSignedIn || !data?.hasDevelopmentHardwareAccess || !data.isHubAdmin) {
      return;
    }

    let active = true;
    fetch(addressesUrl)
      .then(async response => {
        if (!response.ok) {
          throw new Error(`Failed to load billing account options: ${response.status}`);
        }
        return (await response.json()) as DeviceOrderingAddressesResponse;
      })
      .then(response => {
        if (active) {
          const existingBillingAccount = response.billingAccounts.find(
            account => account.billingAccountId === billingAccountId
          );
          setOptions(response);
          setBillingAccount(existingBillingAccount);
          setForm(
            existingBillingAccount
              ? {
                  locationName: existingBillingAccount.name,
                  financialContactId: existingBillingAccount.financialContactId ?? "",
                  countryId: existingBillingAccount.countryId ?? "",
                  line1: existingBillingAccount.mainAddress.line1,
                  line2: existingBillingAccount.mainAddress.line2 ?? "",
                  city: existingBillingAccount.mainAddress.city,
                  stateOrProvince: existingBillingAccount.mainAddress.stateOrProvince,
                  postalCode: existingBillingAccount.mainAddress.postalCode,
                  phone: existingBillingAccount.mainAddress.phone ?? "",
                  taxIdType: existingBillingAccount.taxIdType,
                  vatId: existingBillingAccount.vatId ?? "",
                  taxIdNumber: existingBillingAccount.taxIdNumber ?? "",
                  vatLiable: existingBillingAccount.vatLiable === true,
                  chineseGoldenAddress: existingBillingAccount.chineseGoldenAddress ?? "",
                }
              : {
                  ...emptyForm,
                  financialContactId:
                    response.primaryFinancialContactId &&
                    response.financialContacts.some(
                      contact => contact.id === response.primaryFinancialContactId
                    )
                      ? response.primaryFinancialContactId
                      : "",
                }
          );
          const existingCountry = response.countriesOrRegions.find(
            country => country.id === existingBillingAccount?.countryId
          );
          setHasVatId(
            existingCountry?.region === "emea" && existingBillingAccount
              ? existingBillingAccount.vatLiable
                ? false
                : true
              : undefined
          );
          setChileVatStatus(
            existingCountry?.isoCode === "CL" && existingBillingAccount
              ? existingBillingAccount.taxIdNumber
                ? "exempt"
                : ""
              : ""
          );
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setError(submitError);
          setIsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [
    billingAccountId,
    data?.hasDevelopmentHardwareAccess,
    data?.isHubAdmin,
    isPageReady,
    isSignedIn,
    submitError,
  ]);

  const update = (field: keyof CreateBillingAccountRequest, value: string) => {
    setForm(current => ({ ...current, [field]: value }));
  };

  useEffect(() => {
    if (!billingRule) {
      return;
    }

    setForm(current => {
      const vatId = billingRule.taxId.field === "vatId" ? current.vatId : "";
      const taxIdNumber = billingRule.taxId.field === "taxIdNumber" ? current.taxIdNumber : "";
      const taxIdType = billingRule.taxId.partnerSelectsType
        ? current.taxIdType
        : billingRule.computedValues.taxIdType;
      return { ...current, vatId, taxIdNumber, taxIdType };
    });
  }, [billingRule]);

  const updateCountry = (countryId: string) => {
    setHasVatId(undefined);
    setChileVatStatus("");
    setSupportingDocuments([]);
    setForm(current => ({
      ...current,
      countryId,
      taxIdType: undefined,
      vatId: "",
      taxIdNumber: "",
      vatLiable: false,
      chineseGoldenAddress: "",
    }));
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const requiredValues = [
      form.locationName,
      form.financialContactId,
      form.line1,
      form.city,
      form.stateOrProvince,
      form.postalCode,
      form.phone,
      form.countryId,
    ];
    if (requiredValues.some(value => !value.trim())) {
      setError(requiredFieldsError);
      return;
    }
    const billingQuestionMissing =
      billingRule?.questions.includes("hasVatId") && hasVatId === undefined
        ? true
        : billingRule?.questions.includes("chileVatStatus") && !chileVatStatus;
    const taxIdValue =
      billingRule?.taxId.field === "vatId"
        ? form.vatId
        : billingRule?.taxId.field === "taxIdNumber"
        ? form.taxIdNumber
        : "";
    if (
      billingQuestionMissing ||
      (billingRule?.taxId.requirement === "required" && !taxIdValue?.trim()) ||
      (billingRule?.taxId.partnerSelectsType && form.taxIdType === undefined)
    ) {
      setError(requiredFieldsError);
      return;
    }
    if (
      (form.chineseGoldenAddress?.length ?? 0) > 2000 ||
      supportingDocuments.some(document => document.file.size > maxSupportingDocumentBytes)
    ) {
      setError(submitError);
      return;
    }

    setError("");
    setIsSubmitting(true);
    try {
      let persistedBillingAccountId = savedBillingAccountId;
      if (!persistedBillingAccountId) {
        const response = await fetch(getBillingAccountUrl(billingAccountId), {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...form,
            vatId: billingRule?.computedValues.vatId ?? form.vatId,
          }),
        });
        if (!response.ok) {
          throw new Error(`Failed to save billing account: ${response.status}`);
        }
        const savedAccount = (await response.json()) as Readonly<{ billingAccountId: string }>;
        persistedBillingAccountId = savedAccount.billingAccountId;
        if (supportingDocuments.length > 0) {
          setSavedBillingAccountId(persistedBillingAccountId);
        }
      }
      for (const document of supportingDocuments) {
        const file = document.file;
        const response = await fetch(
          getBillingAccountFileUrl(persistedBillingAccountId, document.id),
          {
            method: "PUT",
            headers: {
              "Content-Type": file.type || "application/octet-stream",
              "X-File-Name": encodeURIComponent(file.name),
            },
            body: file,
          }
        );
        if (!response.ok) {
          throw new Error(`Failed to upload billing account file: ${response.status}`);
        }
        setSupportingDocuments(current =>
          current.filter(candidate => candidate.id !== document.id)
        );
      }
      history.push(partnerSectionLinks.deviceOrderingAddresses.to);
    } catch {
      setError(submitError);
      setIsSubmitting(false);
    }
  };

  if (!isPageReady || !isSignedIn || !data || isLoading) {
    return (
      <PartnerPageLayout breadcrumbLabel={billingInformationTitle}>
        <PageLoadingSpinner />
      </PartnerPageLayout>
    );
  }
  if (!data.isHubAdmin || !data.hasDevelopmentHardwareAccess) {
    return null;
  }

  const countryDependentFields: ReadonlyArray<
    Readonly<{
      field: "line1" | "line2" | "city" | "stateOrProvince" | "postalCode" | "phone";
      label: string;
      required: boolean;
      type?: string;
    }>
  > = [
    { field: "line1", label: addressLine1Label, required: true },
    { field: "line2", label: addressLine2Label, required: false },
    { field: "city", label: cityLabel, required: true },
    { field: "stateOrProvince", label: stateLabel, required: true },
    { field: "postalCode", label: postalCodeLabel, required: true },
    { field: "phone", label: phoneLabel, required: true, type: "tel" },
  ];
  const selectedFinancialContact =
    options?.financialContacts.find(contact => contact.id === form.financialContactId) ??
    (billingAccount
      ? {
          id: billingAccount.financialContactId ?? billingAccount.billingAccountId,
          name: billingAccount.financialContactName,
          email: billingAccount.financialContactEmail,
        }
      : undefined);
  const isReadOnly =
    billingAccount !== undefined &&
    billingAccount.storeStatus !== DevelopmentHardwareStoreStatus.Open;
  const isFormLocked = isReadOnly || !!savedBillingAccountId;
  const existingDocuments = billingAccount?.supportingDocuments ?? [];
  const readOnlyStatusLabel =
    billingAccount?.storeStatus === DevelopmentHardwareStoreStatus.Pending
      ? underReviewLabel
      : closedStatusLabel;
  const readOnlyStatusClass =
    billingAccount?.storeStatus === DevelopmentHardwareStoreStatus.Pending
      ? styles.statusPending
      : styles.statusClosed;
  const openFinancialContactModal = () => {
    setPendingFinancialContactId(form.financialContactId);
    setIsFinancialContactModalOpen(true);
  };
  const closeFinancialContactModal = () => {
    setPendingFinancialContactId(form.financialContactId);
    setIsFinancialContactModalOpen(false);
  };
  const confirmFinancialContact = () => {
    if (!pendingFinancialContactId) {
      return;
    }
    update("financialContactId", pendingFinancialContactId);
    setIsFinancialContactModalOpen(false);
  };
  const updateBillingQuestion = (question: BillingRuleQuestion, value: string) => {
    if (question === "hasVatId") {
      const nextHasVatId = value === "yes";
      setHasVatId(nextHasVatId);
      setSupportingDocuments([]);
      setForm(current => ({
        ...current,
        vatId: "",
        taxIdType: undefined,
        vatLiable: !nextHasVatId,
      }));
      return;
    }
    setChileVatStatus(value as ChileVatStatus);
  };
  const getBillingRuleString = (key: string): string =>
    billingRuleStrings[key as keyof typeof billingRuleStrings] ?? key;

  return (
    <PartnerPageLayout
      breadcrumbLabel={billingInformationTitle}
      breadcrumbSteps={[{ label: pageLabel, to: partnerSectionLinks.deviceOrderingAddresses.to }]}
    >
      <div className={styles.page}>
        <form className={styles.form} aria-busy={isSubmitting} onSubmit={submit}>
          <header className={styles.pageHeader}>
            <div>
              <h1>{billingInformationTitle}</h1>
              <p>{billingInformationDescription}</p>
            </div>
            <div className={styles.actions}>
              <BasicButton
                as="button"
                buttonType={ButtonType.Secondary}
                type="button"
                disabled={isSubmitting}
                onClick={() => history.push(partnerSectionLinks.deviceOrderingAddresses.to)}
              >
                {cancelLabel}
              </BasicButton>
              {!isReadOnly && (
                <BasicButton
                  as="button"
                  buttonType={ButtonType.Brand}
                  type="submit"
                  disabled={isSubmitting || !form.countryId}
                >
                  {submitLabel}
                </BasicButton>
              )}
            </div>
          </header>

          <div className={styles.formCard}>
            <section className={styles.financialContactSection}>
              <div className={styles.sectionHeader}>
                <div>
                  <h2>
                    {financialContactLabel}
                    <span className={styles.requiredMark} aria-hidden="true">
                      *
                    </span>
                  </h2>
                  <p>{financialContactDescription}</p>
                </div>
                {isReadOnly ? (
                  <div
                    className={styles.accountStatus}
                    aria-label={`${statusLabel}: ${readOnlyStatusLabel}`}
                  >
                    <span
                      className={`${styles.statusDot} ${readOnlyStatusClass}`}
                      aria-hidden="true"
                    />
                    <span>{readOnlyStatusLabel}</span>
                  </div>
                ) : (
                  <BasicButton
                    as="button"
                    buttonType={ButtonType.Tertiary}
                    type="button"
                    disabled={isSubmitting}
                    onClick={openFinancialContactModal}
                  >
                    {changeFinancialContactLabel}
                  </BasicButton>
                )}
              </div>

              {selectedFinancialContact && (
                <div className={styles.contactCard}>
                  <span
                    className={`${styles.contactAvatar} ${getAvatarClass(
                      selectedFinancialContact.id
                    )}`}
                    aria-hidden="true"
                  >
                    {getContactInitials(
                      selectedFinancialContact.name,
                      selectedFinancialContact.email
                    )}
                  </span>
                  <span className={styles.contactDetails}>
                    <strong>{selectedFinancialContact.name}</strong>
                    {selectedFinancialContact.email && (
                      <span>{selectedFinancialContact.email}</span>
                    )}
                  </span>
                </div>
              )}
            </section>

            <div className={styles.accountDetailsSection}>
              <div className={styles.field}>
                <label htmlFor="billing-account-locationName">
                  {billingAccountNameLabel}
                  <span className={styles.requiredMark} aria-hidden="true">
                    *
                  </span>
                </label>
                <input
                  id="billing-account-locationName"
                  required
                  aria-required="true"
                  disabled={isFormLocked}
                  value={form.locationName}
                  onChange={event => update("locationName", event.target.value)}
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="billing-account-country">
                  {countryLabel}
                  <span className={styles.requiredMark} aria-hidden="true">
                    *
                  </span>
                </label>
                <select
                  id="billing-account-country"
                  required
                  aria-required="true"
                  disabled={isFormLocked}
                  value={form.countryId}
                  onChange={event => updateCountry(event.target.value)}
                >
                  <option value="">{selectLabel}</option>
                  {countries.map(country => (
                    <option key={country.value} value={country.value}>
                      {country.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {form.countryId && (
              <div className={styles.detailsGrid}>
                {countryDependentFields.map(({ field, label, required, type }) => (
                  <div className={styles.field} key={field}>
                    <label htmlFor={`billing-account-${field}`}>
                      {label}
                      {required && (
                        <span className={styles.requiredMark} aria-hidden="true">
                          *
                        </span>
                      )}
                    </label>
                    <input
                      id={`billing-account-${field}`}
                      type={type ?? "text"}
                      required={required}
                      aria-required={required || undefined}
                      disabled={isFormLocked}
                      value={form[field]}
                      onChange={event => update(field, event.target.value)}
                    />
                  </div>
                ))}
              </div>
            )}

            {form.countryId && billingRule && showTaxSection && (
              <section className={styles.taxSection}>
                <h2>{getBillingRuleString("developmentHardwareTaxInformationTitle")}</h2>

                {billingRule.questions.map(question => {
                  const isVatQuestion = question === "hasVatId";
                  const value = isVatQuestion
                    ? hasVatId === undefined
                      ? ""
                      : hasVatId
                      ? "yes"
                      : "no"
                    : chileVatStatus;
                  const questionLabel = getBillingRuleString(
                    isVatQuestion
                      ? "developmentHardwareHasVatIdQuestion"
                      : "developmentHardwareChileVatStatusQuestion"
                  );
                  const optionsForQuestion = isVatQuestion
                    ? [
                        { value: "yes", label: yesLabel },
                        { value: "no", label: noLabel },
                      ]
                    : [
                        {
                          value: "exempt",
                          label: getBillingRuleString("developmentHardwareVatExempt"),
                        },
                        {
                          value: "liable",
                          label: getBillingRuleString("developmentHardwareVatLiable"),
                        },
                      ];
                  return (
                    <fieldset className={styles.taxQuestion} key={question}>
                      <legend>
                        {questionLabel}
                        <span className={styles.requiredMark} aria-hidden="true">
                          *
                        </span>
                      </legend>
                      <div className={styles.radioGroup}>
                        {optionsForQuestion.map(option => (
                          <label className={styles.radioOption} key={option.value}>
                            <input
                              type="radio"
                              name={`billing-account-${question}`}
                              value={option.value}
                              checked={value === option.value}
                              disabled={isFormLocked}
                              onChange={event =>
                                updateBillingQuestion(question, event.target.value)
                              }
                            />
                            <span>{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  );
                })}

                {billingRule.taxId.partnerSelectsType && (
                  <div className={styles.field}>
                    <label htmlFor="billing-account-taxIdType">
                      {getBillingRuleString("developmentHardwareTaxIdType")}
                      <span className={styles.requiredMark} aria-hidden="true">
                        *
                      </span>
                    </label>
                    <select
                      id="billing-account-taxIdType"
                      required
                      aria-required="true"
                      disabled={isFormLocked}
                      value={form.taxIdType ?? ""}
                      onChange={event =>
                        setForm(current => ({
                          ...current,
                          taxIdType: event.target.value
                            ? (Number(event.target.value) as TaxIdType)
                            : undefined,
                        }))
                      }
                    >
                      <option value="">{selectLabel}</option>
                      {billingRule.taxId.allowedTypes?.map(type => (
                        <option key={String(type)} value={getBillingTaxIdTypeValue(type)}>
                          {getBillingRuleString(
                            type === "IndiaGst"
                              ? "developmentHardwareIndiaGst"
                              : "developmentHardwareIndiaPan"
                          )}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {billingRule.taxId.field !== "none" && (
                  <div className={styles.field}>
                    <label htmlFor={`billing-account-${billingRule.taxId.field}`}>
                      {getBillingRuleString(
                        getBillingTaxLabelKey(
                          selectedCountry?.isoCode ?? "",
                          billingRule.taxId.field,
                          data.studio?.isSoleProprietor === true
                        )
                      )}
                      {billingRule.taxId.requirement === "required" && (
                        <span className={styles.requiredMark} aria-hidden="true">
                          *
                        </span>
                      )}
                    </label>
                    <input
                      id={`billing-account-${billingRule.taxId.field}`}
                      required={billingRule.taxId.requirement === "required"}
                      aria-required={billingRule.taxId.requirement === "required" || undefined}
                      disabled={isFormLocked}
                      value={
                        billingRule.taxId.field === "vatId"
                          ? form.vatId ?? ""
                          : form.taxIdNumber ?? ""
                      }
                      onChange={event => {
                        const field = billingRule.taxId.field;
                        if (field !== "none") {
                          update(field, event.target.value);
                        }
                      }}
                    />
                  </div>
                )}

                {billingRule.additionalDetails === "chinaGoldenTax" && (
                  <div className={styles.field}>
                    <label htmlFor="billing-account-golden-tax-information">
                      {getBillingRuleString("developmentHardwareGoldenTaxInformation")}
                    </label>
                    <textarea
                      id="billing-account-golden-tax-information"
                      disabled={isFormLocked}
                      maxLength={2000}
                      value={form.chineseGoldenAddress ?? ""}
                      onChange={event => update("chineseGoldenAddress", event.target.value)}
                    />
                  </div>
                )}

                {billingRule.attachment && (
                  <div className={styles.documentsSection}>
                    <h3>{getBillingRuleString("developmentHardwareSupportingDocuments")}</h3>
                    <p className={styles.documentGuidance}>
                      {getBillingRuleString(
                        getBillingDocumentGuidanceKey(
                          selectedCountry?.isoCode ?? "",
                          data.studio?.isSoleProprietor === true
                        )
                      )}
                    </p>
                    <label
                      className={styles.documentDropzone}
                      htmlFor="billing-account-supporting-documents"
                      onDragOver={event => event.preventDefault()}
                      onDrop={event => {
                        event.preventDefault();
                        if (!isFormLocked) {
                          setSupportingDocuments(
                            Array.from(event.dataTransfer.files).map(file => ({
                              id: uuidv4(),
                              file,
                            }))
                          );
                        }
                      }}
                    >
                      <Icon aria-hidden="true" icon={Upload} iconSize={IconSize.Custom} />
                      <span>
                        {getBillingRuleString("welcomePartnerDragAndDropInstructions")}{" "}
                        <span className={styles.browseFiles}>
                          {getBillingRuleString("welcomePartnerFileUploadBrowseFile")}
                        </span>
                      </span>
                      <input
                        id="billing-account-supporting-documents"
                        className={styles.documentInput}
                        type="file"
                        multiple
                        aria-label={getBillingRuleString("developmentHardwareSupportingDocuments")}
                        disabled={isFormLocked}
                        onChange={event =>
                          setSupportingDocuments(
                            Array.from(event.target.files ?? []).map(file => ({
                              id: uuidv4(),
                              file,
                            }))
                          )
                        }
                      />
                    </label>
                    {existingDocuments.length > 0 && (
                      <ul className={styles.fileList}>
                        {existingDocuments.map(file => (
                          <li key={file.id}>{file.fileName || file.name}</li>
                        ))}
                      </ul>
                    )}
                    {supportingDocuments.length > 0 && (
                      <ul className={styles.fileList}>
                        {supportingDocuments.map(document => (
                          <li key={document.id}>{document.file.name}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </section>
            )}

            {!billingRule?.attachment && existingDocuments.length > 0 && (
              <section className={styles.documentsSection}>
                <h2>{getBillingRuleString("developmentHardwareSupportingDocuments")}</h2>
                <ul className={styles.fileList}>
                  {existingDocuments.map(file => (
                    <li key={file.id}>{file.fileName || file.name}</li>
                  ))}
                </ul>
              </section>
            )}

            {error && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}
          </div>
        </form>

        {!isReadOnly && (
          <Modal
            isOpen={isFinancialContactModalOpen}
            onClose={closeFinancialContactModal}
            width={34 * 16}
            ariaLabelledBy="billing-account-financial-contact-modal-title"
            initialFocus={ModalInitialFocus.FirstInput}
          >
            <div className={styles.contactModal}>
              <h2 id="billing-account-financial-contact-modal-title">
                {changeFinancialContactTitle}
              </h2>
              <p>{financialContactDescription}</p>
              <fieldset className={styles.contactOptions}>
                <legend>{financialContactLabel}</legend>
                <div className={styles.contactOptionsList}>
                  {options?.financialContacts.map(contact => {
                    const isSelected = pendingFinancialContactId === contact.id;
                    return (
                      <label
                        className={`${styles.contactOption}${
                          isSelected ? ` ${styles.contactOptionSelected}` : ""
                        }`}
                        key={contact.id}
                      >
                        <span
                          className={`${styles.contactAvatar} ${getAvatarClass(contact.id)}`}
                          aria-hidden="true"
                        >
                          {getContactInitials(contact.name, contact.email)}
                        </span>
                        <span className={styles.contactDetails}>
                          <strong>{contact.name}</strong>
                          {contact.email && <span>{contact.email}</span>}
                        </span>
                        <input
                          type="radio"
                          name="billing-account-financial-contact"
                          value={contact.id}
                          checked={isSelected}
                          onChange={event => setPendingFinancialContactId(event.target.value)}
                        />
                      </label>
                    );
                  })}
                </div>
              </fieldset>
              <div className={styles.modalActions}>
                <BasicButton
                  as="button"
                  buttonType={ButtonType.Secondary}
                  type="button"
                  onClick={closeFinancialContactModal}
                >
                  {cancelLabel}
                </BasicButton>
                <BasicButton
                  as="button"
                  buttonType={ButtonType.Brand}
                  type="button"
                  disabled={!pendingFinancialContactId}
                  onClick={confirmFinancialContact}
                >
                  {changeFinancialContactLabel}
                </BasicButton>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </PartnerPageLayout>
  );
};
